import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { WEBSITE_ARTICLES, WEBSITE_KNOWLEDGE_ITEMS, WEBSITE_USE_CASES } from './websiteData';
import { KnowledgeSignalIllustration } from './WebsiteIllustrations';

function uniqueCategories(items) {
  return ['All', ...Array.from(new Set(items.map((item) => item.category)))];
}

export default function WebsiteKnowledgeHubPage() {
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [category, setCategory] = useState('All');

  const categories = useMemo(() => uniqueCategories(WEBSITE_KNOWLEDGE_ITEMS), []);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return WEBSITE_KNOWLEDGE_ITEMS.filter((item) => {
      const categoryMatch = category === 'All' || item.category === category;
      const queryMatch =
        !needle ||
        [item.title, item.category, item.description, ...(item.keywords || [])]
          .join(' ')
          .toLowerCase()
          .includes(needle);
      return categoryMatch && queryMatch;
    });
  }, [category, query]);

  return (
    <div className="dgw-page-wrap">
      <section className="dgw-hero">
        <div className="dgw-hero-inner">
          <div className="dgw-badge">
            <span className="dgw-badge-dot" />
            Knowledge Hub
          </div>
          <h1>Searchable public answers about DepGuard</h1>
          <p className="dgw-hero-lead">
            This is the customer-friendly information layer: product pages, FAQ, commercial entry points,
            and portal access in one searchable surface.
          </p>
        </div>
        <aside className="dgw-panel">
          <KnowledgeSignalIllustration />
          <p className="dgw-illustration-caption">
            A curated knowledge surface should help buyers and engineers find the right answer quickly.
          </p>
        </aside>
      </section>

      <section className="dgw-section">
        <div className="dgw-section-header">
          <div className="dgw-section-header-row">
            <div className="dgw-section-header-copy">
              <span className="dgw-section-eyebrow">Editorial content</span>
              <h2>Featured articles and use cases</h2>
              <p>Give the knowledge hub real substance so it feels like a public answer surface, not only a list of links.</p>
            </div>
            <div className="dgw-section-detail">The knowledge hub should feel closer to a curated library than a utilitarian help page.</div>
          </div>
        </div>
        <div className="dgw-grid dgw-grid-3">
          {WEBSITE_ARTICLES.map((article) => (
            <Link key={article.slug} className="dgw-card dgw-card-link dgw-card-emphasis" to={`/website/knowledge/${article.slug}`}>
              <span className="dgw-card-kicker">{article.category}</span>
              <h3>{article.title}</h3>
              <p>{article.summary}</p>
            </Link>
          ))}
        </div>

        <div className="dgw-section-header" style={{ marginTop: 28 }}>
          <span className="dgw-section-eyebrow">Buyer scenarios</span>
          <h2>Customer use cases</h2>
        </div>
        <div className="dgw-grid dgw-grid-3">
          {WEBSITE_USE_CASES.map((useCase) => (
            <article key={useCase.title} className="dgw-card">
              <span className="dgw-card-topline">Use case</span>
              <h3>{useCase.title}</h3>
              <p>{useCase.desc}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="dgw-section">
        <div className="dgw-section-header">
          <span className="dgw-section-eyebrow">Search and discovery</span>
          <h2>Browse the knowledge surface</h2>
          <p>Move between editorial content, commercial answers, and navigation entry points from one quiet interface.</p>
        </div>
        <div className="dgw-search-shell">
          <input
            className="dgw-search-input"
            type="search"
            placeholder="Search product, pricing direction, FAQ, company story, demo, trial..."
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <div className="dgw-search-meta">{filtered.length} result{filtered.length === 1 ? '' : 's'}</div>
        </div>

        <div className="dgw-filter-row">
          {categories.map((item) => (
            <button
              key={item}
              type="button"
              className={`dgw-filter-chip${category === item ? ' active' : ''}`}
              onClick={() => setCategory(item)}
            >
              {item}
            </button>
          ))}
        </div>

        <div className="dgw-grid dgw-grid-2">
          {filtered.map((item) => (
            <Link key={`${item.category}-${item.title}`} className="dgw-card dgw-card-link" to={item.route}>
              <span className="dgw-card-kicker">{item.category}</span>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </Link>
          ))}
        </div>

        {!filtered.length && (
          <div className="dgw-empty">
            No result yet. Try terms like <code>trial</code>, <code>product</code>, <code>company</code>,{' '}
            <code>FAQ</code>, or <code>demo</code>.
          </div>
        )}
      </section>
    </div>
  );
}
